/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/useEmailPreviewPane.ts
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-CONTROLLER-001
 * PURPOSE: Coordinate preview state and expose typed leaf-component props.
 */
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  getSamKanbanApi,
  resolveKanbanOrgId,
} from '@quotech/kanban-host'
import { useEmailActions } from '../../hooks/useEmails'
import { useToast } from '../../../_internal/components/Toast'
import { useApiAuth } from '../../../_internal/components/ApiAuthGate'
import {
  getQuotechOrgIdOverride,
  getRuntimeQuotechOrgId,
} from '../../../_internal/services/quotechApi'
import {
  buildKanbanBoardLink,
  buildKanbanCardLink,
} from '../../lib/kanbanDeepLink'
import { pickInvoiceAttachment } from '../BillDocDrawer'
import { usePreviewBody } from './usePreviewBody'
import { usePreviewShortcuts } from './usePreviewShortcuts'
import { usePreviewToolbarActions } from './usePreviewToolbarActions'
import type {
  EmailPreviewPaneProps,
  PreviewHeaderProps,
  PreviewOverlaysProps,
  PreviewReadingSurfaceProps,
} from './types'

export interface EmailPreviewPaneController {
  headerProps: PreviewHeaderProps
  readingProps: PreviewReadingSurfaceProps
  overlayProps: PreviewOverlaysProps
}

export function useEmailPreviewPane(
  props: EmailPreviewPaneProps,
): EmailPreviewPaneController {
  const {
    email,
    onClose,
    showCloseAction = true,
    onFullView,
    onRefetch,
    onPatchEmail,
    onResolved,
    onBeginTerminalRemoval,
    onOpenInMarkup,
    onContextMenu,
    onFilterCompany,
    allocationMembers,
    bodyLoading,
    detailError,
    onRetryDetail,
    navigation,
    collapseOlderMessages,
  } = props
  const navigate = useNavigate()
  const { archive, bulkAction, toggleStar } = useEmailActions()
  const { toast } = useToast()
  const { session } = useApiAuth()
  const kanbanOrgId = resolveKanbanOrgId(session, {
    runtimeId: getRuntimeQuotechOrgId(),
    buildtimeId: getQuotechOrgIdOverride(),
  })
  const kanbanApi = useMemo(() => getSamKanbanApi(), [])
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false)
  const [peekCard, setPeekCard] = useState<{
    cardId: string
    boardId: string | null
  } | null>(null)
  const [peekJob, setPeekJob] = useState<number | null>(null)
  const [peekBoardOpen, setPeekBoardOpen] = useState(false)
  const [billDocOpen, setBillDocOpen] = useState(false)
  const [pingOpen, setPingOpen] = useState(false)
  const [allocateOpen, setAllocateOpen] = useState(false)
  const canRequestRule =
    typeof kanbanApi.proposeRuleRequest === 'function' &&
    typeof kanbanApi.applyRuleRequest === 'function' &&
    kanbanOrgId != null

  const openAllocatedCard = useCallback(() => {
    const summary = email.allocation_summary
    if (summary?.card_id) {
      setPeekCard({
        cardId: summary.card_id,
        boardId: summary.board_id ?? null,
      })
      return
    }
    const link = buildKanbanCardLink(summary)
    if (link) navigate(link)
  }, [email.allocation_summary, navigate])

  const navigateToJob = useCallback(() => {
    if (peekJob != null) {
      setPeekJob(null)
      navigate(`/jobs/${peekJob}`)
    }
  }, [navigate, peekJob])

  const navigateToBoard = useCallback(() => {
    setPeekBoardOpen(false)
    const link = buildKanbanBoardLink(email.allocation_summary)
    if (link) navigate(link)
  }, [email.allocation_summary, navigate])

  const openAllocatedBoard = useCallback(() => {
    if (email.allocation_summary?.board_id) {
      setPeekBoardOpen(true)
      return
    }
    const link = buildKanbanBoardLink(email.allocation_summary)
    if (link) navigate(link)
  }, [email.allocation_summary, navigate])

  const openBillDoc = useCallback(() => {
    if (!pickInvoiceAttachment(email.attachments)) {
      toast('No invoice document is attached to this email.', 'info')
      return
    }
    setBillDocOpen(true)
  }, [email.attachments, toast])

  usePreviewShortcuts({
    email,
    kanbanApi,
    archive,
    toast,
    navigate,
    onRefetch,
    onResolved,
    setPingOpen,
    setPeekBoardOpen,
    setAllocateOpen,
  })
  const body = usePreviewBody(email.id, onRefetch)
  const toolbar = usePreviewToolbarActions({
    email,
    kanbanOrgId: kanbanOrgId ?? null,
    archive,
    bulkAction,
    toggleStar,
    toast,
    findBodyRef: body.findBodyRef,
    onRefetch,
    onResolved,
    setShowLinkModal,
  })

  return {
    headerProps: {
      email,
      onClose,
      showCloseAction,
      onFullView,
      onRefetch,
      pingOpen,
      setPingOpen,
      navigation,
      ...toolbar,
      find: body.find,
    },
    readingProps: {
      email,
      onContextMenu,
      onRefetch,
      onPatchEmail,
      onResolved,
      onBeginTerminalRemoval,
      onFilterCompany,
      allocationMembers,
      bodyLoading,
      detailError,
      onRetryDetail,
      kanbanApi,
      kanbanOrgId: kanbanOrgId ?? null,
      canRequestRule,
      openAllocatedCard,
      openAllocatedBoard,
      openBillDoc,
      setPeekJob,
      setRuleDialogOpen,
      combinedBodyRef: body.combinedBodyRef,
      find: body.find,
      collapseOlderMessages,
    },
    overlayProps: {
      email,
      onRefetch,
      onOpenInMarkup,
      showLinkModal,
      setShowLinkModal,
      imageInteractions: body.imageInteractions,
      peekCard,
      setPeekCard,
      peekJob,
      setPeekJob,
      navigateToJob,
      peekBoardOpen,
      setPeekBoardOpen,
      navigateToBoard,
      billDocOpen,
      setBillDocOpen,
      ruleDialogOpen,
      setRuleDialogOpen,
      canRequestRule,
      kanbanApi,
      kanbanOrgId: kanbanOrgId ?? null,
      allocateOpen,
      setAllocateOpen,
    },
  }
}
